import React from 'react'

const ArtistBubble = ({
  artist,
  index,
  isOwner,
  isInFavorites = false,
  isFutureEvent = false,
  onToggle,
  onCommentClick,
  showSeparator = false
}) => {
  const handleClick = (field, value) => {
    if (!isOwner) return

    if (field === 'comment') {
      onCommentClick(index)
    } else if (field === 'remove') {
      if (window.confirm(`Remove ${artist.name} from artists?`)) {
        onToggle(index, 'remove')
      }
    } else {
      onToggle(index, field)
    }
  }

  const handleBubbleClick = () => {
    if (!isOwner) return

    if (isFutureEvent) {
      // For future events, toggle wantToSee
      onToggle(index, 'wantToSee')
    } else {
      // For past events, cycle through states
      if (!artist.wantToSee && !artist.seen) {
        // Default → Wanted to see
        onToggle(index, 'wantToSee')
      } else if (artist.wantToSee && !artist.seen) {
        // Wanted to see → Saw them
        onToggle(index, 'wantToSee') // Turn off wantToSee
        setTimeout(() => onToggle(index, 'seen'), 50) // Turn on seen
      } else if (artist.seen) {
        // Saw them → Default
        onToggle(index, 'seen')
      }
    }
  }

  // Determine bubble style based on state
  let bubbleStyle = `inline-block px-2 py-1 rounded-full border-2 transition-all duration-300 ease-in-out ${
    isOwner ? 'cursor-pointer' : 'cursor-default'
  } `

  if (isFutureEvent && artist.wantToSee) {
    bubbleStyle += 'bg-purple-500/20 border-purple-500/60 text-purple-300'
  } else if (!isFutureEvent && artist.seen) {
    bubbleStyle += isInFavorites
      ? 'bg-green-500/30 border-green-400/60 text-green-200'
      : 'bg-green-500/20 border-green-500/60 text-green-300'
  } else if (!isFutureEvent && artist.wantToSee && !artist.seen) {
    bubbleStyle += 'bg-amber-500/20 border-amber-500/60 text-amber-300'
  } else {
    bubbleStyle += isInFavorites
      ? 'border-yellow-400/30 text-yellow-200'
      : 'border-transparent hover:border-white/30'
  }

  if (artist.comment) {
    bubbleStyle += isInFavorites ? ' bg-blue-500/20 border-blue-500/40' : ' bg-blue-500/10 border-blue-500/30'
  }

  const getTooltipText = () => {
    if (!isOwner) {
      return `${artist.name}${artist.performanceDate ? `\nDate: ${new Date(artist.performanceDate).toLocaleDateString()}` : ''}${artist.setTime ? `\nSet Time: ${artist.setTime}` : ''}${artist.performance ? `\nDetails: ${artist.performance}` : ''}${artist.comment ? `\nComment: ${artist.comment}` : ''}`
    }

    if (isFutureEvent) {
      const currentState = artist.wantToSee ? 'Want to see this artist!' : 'Not marked'
      const nextState = artist.wantToSee ? 'Remove from wishlist' : 'Add to wishlist'
      return `${currentState}\nClick to: ${nextState}${artist.performanceDate ? `\nDate: ${new Date(artist.performanceDate).toLocaleDateString()}` : ''}${artist.setTime ? `\nSet Time: ${artist.setTime}` : ''}${artist.performance ? `\nDetails: ${artist.performance}` : ''}${artist.comment ? `\nComment: ${artist.comment}` : ''}`
    }

    // Past event cycling tooltip
    let currentState, nextState
    if (!artist.wantToSee && !artist.seen) {
      currentState = 'Not marked'
      nextState = 'wanted to see but missed'
    } else if (artist.wantToSee && !artist.seen) {
      currentState = 'Wanted to see but missed'
      nextState = 'saw them'
    } else if (artist.seen) {
      currentState = 'Saw them'
      nextState = 'not marked'
    }

    return `${currentState}\nClick to mark as: ${nextState}${artist.performanceDate ? `\nDate: ${new Date(artist.performanceDate).toLocaleDateString()}` : ''}${artist.setTime ? `\nSet Time: ${artist.setTime}` : ''}${artist.performance ? `\nDetails: ${artist.performance}` : ''}${artist.comment ? `\nComment: ${artist.comment}` : ''}`
  }

  return (
    <span className="inline-block group relative mr-2 mb-1">
      <span
        onClick={handleBubbleClick}
        className={bubbleStyle}
        title={getTooltipText()}
      >
        {artist.name}
        {artist.setTime && <span className="ml-1 text-green-300">🕐</span>}
        {artist.comment && <span className="ml-1 text-blue-300">💬</span>}
        {isFutureEvent && artist.wantToSee && <span className="ml-1 text-purple-300">👁</span>}
      </span>

      {isOwner && (
        <>
          {/* Edit button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClick('comment')
            }}
            className="absolute -top-1 -right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
            title="Edit artist details"
          >
            <div className="w-4 h-4 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-full">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
          </button>

          {/* Star checkbox */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClick('favorite')
            }}
            className="absolute -top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              {artist.favorite ? (
                <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400 hover:text-yellow-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              )}
            </div>
          </button>

          {/* Remove button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClick('remove')
            }}
            className="absolute -top-1 -left-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <div className="w-4 h-4 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-full text-xs">
              ✕
            </div>
          </button>
        </>
      )}

      {showSeparator && <span className="text-gray-500"> </span>}
    </span>
  )
}

export default ArtistBubble